import axios, { AxiosError } from "axios";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

/**
 * Shared axios instance.
 *
 * - ``withCredentials: true`` sends + accepts the ``aiaso_session``
 *   cookie that the backend issues on signup / login. Required for
 *   every endpoint that reads the auth context.
 * - The response interceptor preserves the original axios error on the
 *   rejected promise so call sites can branch on ``error.response?.status``;
 *   the existing string-only fallback is kept for backwards compatibility
 *   with hooks that only show ``error.message``.
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

export interface ApiErrorBody {
  error?: string;
  detail?: unknown;
  message?: string;
  reason?: string;
  scope?: string;
  required_one_of?: string[];
  actual_role?: string;
}

export class ApiError extends Error {
  public readonly status: number | null;
  public readonly body: ApiErrorBody | null;
  public readonly code: string | null;
  /**
   * Seconds the caller should wait before retrying, read off a 429
   * response's `Retry-After` header (RFC 6585) or, when a proxy/browser
   * strips that header, a `detail.retry_after_seconds` fallback in the
   * JSON body — the shape `backend/app/api/v1/signal_ingest.py` emits on
   * rate-limit exhaustion. `backend/app/api/v1/mcp_context.py`'s 429 is a
   * bare JSON-RPC error object (`{jsonrpc, id, error: {code, message}}`,
   * no `detail` key) — it relies on the `Retry-After` header alone, so
   * this fallback stays `null` for that endpoint's body today; the SPA
   * does not currently call `POST /api/v1/mcp` directly, so this is
   * dormant, not live-broken. `null` on every non-429 error AND on a 429
   * that (unusually) carries neither signal — callers MUST treat this as
   * possibly-absent and fall back to a generic "try again shortly"
   * message rather than assuming it is always populated.
   */
  public readonly retryAfterSeconds: number | null;

  constructor(
    message: string,
    status: number | null,
    body: ApiErrorBody | null,
    code: string | null,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Parses an RFC 6585 `Retry-After` header value: either delta-seconds
 * (`"30"`) or an HTTP-date (`"Wed, 21 Oct 2015 07:28:00 GMT"`). Returns
 * `null` for anything absent or unparseable rather than guessing — a
 * wrong-but-present number is worse than an honest "unknown" a caller can
 * fall back on.
 */
function parseRetryAfterHeader(value: unknown): number | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
  }
  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) {
    return null;
  }
  const deltaSeconds = Math.ceil((dateMs - Date.now()) / 1000);
  return deltaSeconds > 0 ? deltaSeconds : 0;
}

/**
 * Reads `retry_after_seconds` out of `detail` when it is the structured
 * `{error, retry_after_seconds, message}` object `signal_ingest.py`'s 429
 * handler emits — a fallback for when the `Retry-After` header itself
 * didn't survive the trip (some proxies strip non-CORS-safelisted response
 * headers). `mcp_context.py`'s 429 body has no `detail` key (a bare
 * JSON-RPC error object), so this fallback is a no-op for that endpoint
 * today — see the `ApiError.retryAfterSeconds` doc comment above.
 */
function extractRetryAfterSecondsFromBody(
  data: ApiErrorBody | null,
): number | null {
  const detail = data?.detail;
  if (!detail || typeof detail !== "object") {
    return null;
  }
  const raw = (detail as Record<string, unknown>).retry_after_seconds;
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0
    ? raw
    : null;
}

function extractMessage(detail: unknown): {
  message: string;
  code: string | null;
} {
  if (typeof detail === "string") {
    return { message: detail, code: null };
  }
  // FastAPI's 422 `detail` is a LIST of `{loc, msg, type}` validation items,
  // not an object — read the first item's `msg` rather than falling through
  // to the generic string. Callers that own a translated failure string
  // (the auth pages) must not render this either way: it is a Pydantic
  // sentence, not product copy.
  if (Array.isArray(detail)) {
    const first: unknown = detail[0];
    const msg =
      first && typeof first === "object"
        ? (first as Record<string, unknown>).msg
        : first;
    return {
      message:
        typeof msg === "string" && msg ? msg : "An unexpected error occurred",
      code: null,
    };
  }
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    const code =
      typeof record.error === "string"
        ? record.error
        : typeof record.error_class === "string"
          ? record.error_class
          : null;
    // Prefer the backend's human-readable `message` over the machine `error`
    // code: structured detail is `{error: "<code>", message: "<sentence>"}`
    // (e.g. llm_unavailable → "Connect an LLM key to generate copy."). Reading
    // `error` first surfaced the bare code to the operator; fall back to
    // `error_message` (the `{error_class, error_message}` shape every
    // integration-connector endpoint emits — Google Ads, ASA, AppsFlyer …),
    // then `detail`, then the code, then a generic string.
    const message =
      (typeof record.message === "string" && record.message) ||
      (typeof record.error_message === "string" && record.error_message) ||
      (typeof record.detail === "string" && record.detail) ||
      (typeof record.error === "string" && record.error) ||
      "An unexpected error occurred";
    return { message: String(message), code };
  }
  return { message: "An unexpected error occurred", code: null };
}

api.interceptors.response.use(
  (response) => response,
  (rawError: AxiosError) => {
    const status = rawError.response?.status ?? null;
    const data = (rawError.response?.data ?? null) as ApiErrorBody | null;
    const detail =
      (data && (data.detail ?? data.message ?? data.error)) ??
      rawError.message ??
      "An unexpected error occurred";
    const { message, code } = extractMessage(detail);
    const retryAfterSeconds =
      parseRetryAfterHeader(rawError.response?.headers?.["retry-after"]) ??
      extractRetryAfterSecondsFromBody(data);
    // A request the caller abandoned (a TanStack query unmounted or
    // superseded, its AbortSignal fired) is not an error worth a console
    // line -- the smoke spec reads every new console error as a regression.
    if (status === null && axios.isCancel(rawError)) {
      return Promise.reject(
        new ApiError(message, status, data, code, retryAfterSeconds),
      );
    }
    if (status === null || status >= 500) {
      console.error("API Error:", message);
    }
    return Promise.reject(
      new ApiError(message, status, data, code, retryAfterSeconds),
    );
  },
);
